// DialogProvider.tsx
import { FormControlLabel, Switch, TextField } from "@mui/material";
import Select, { components, useStateManager } from 'react-select';
import { Utils } from "helpers/utils";
import React, { createContext, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { SettingsState } from "../src/redux/settings/types";
import { useRedux } from "hooks";
import { createSelector } from "reselect";
import { changeTab, setOrgDdlValueForPerm } from "../src/redux/actions";
import Loader from "components/Loader";
import { MemberRole, TaskStatus, WpPostInfo, WpPostInfoTag, WpSliceData } from "../src/redux/workplace/wpTypes";
import SelectDropdown from "components/SelectDropdown";
import SelectDropdownEmp from "components/SelectDropdownEmp";
import DatePicker from "react-datepicker";
import SelectDropdownTkProj from "components/SelectDropdownTkProj";
import DatePickerEx from "components/DatePickerEx";
import SelectDropdownTaskStatus from "components/SelectDropdownTaskStatus";
import { TABS, WpModes } from "../src/constants";
import UserAvatar from "components/UserAvatar";
import AppSimpleBar from "components/AppSimpleBar";
import TextBox from "components/TextBox";
import WpTagBadge from "components/WpTagBadge";
import { SavePostInfoParm } from "types";
import { loadPostInfoApi, savePostInfo } from "api/wpapi";
import UserAvatarWithName from "components/UserAvatarWithName";
import { removePostTitleFromList, setPostInfoOnSignalR } from "../src/redux/workplace/wpActions";
import AutoTextArea from "components/AutoTextArea";
import { backdropStyle, modalStyle, inputStyle } from "DialogProviderShare";
import { TaskRecurancePortal } from "DialogProviderTaskRecurrance";

type BasicDialogOptions = {
  title?: string;
  message?: React.ReactNode;
  okText?: string;
  cancelText?: string;
  showCancel?: boolean;
};

type DialogContextType = {
  show: (opts: BasicDialogOptions) => Promise<any>;
  confirm: (message: string, title?: string) => Promise<boolean>;
  showConfirmPassword: (opts?: { title?: string; apiUrl?: string }) => Promise<boolean>;
  showPermConfig: (opts?: { permName: string, entryId?: number | null, permKey: string, defaultAllow?: boolean }) => Promise<boolean>;
  showPostInfo: (opts?: { postId: string, isConvert2TaskMode?: boolean | null, isCreateSubTask?: boolean | null }) => Promise<boolean>;
  showTaskRecurance: (opts?: { postId: string }) => Promise<string>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [opts, setOpts] = useState<BasicDialogOptions | null>(null);
  const [resolver, setResolver] = useState<((value: any) => void) | null>(null);
  const [special, setSpecial] = useState<null | { type: "confirmPassword"; apiUrl?: string; title?: string }>(null);

  const [resolverPerm, setResolverPerm] = useState<((value: any) => void) | null>(null);
  const [specialPerm, setSpecialPerm] = useState<null | { permName: string, entryId: number | null, permKey: string, defaultAllow: boolean }>(null);

  const [resolverPost, setResolverPost] = useState<((valud: any) => void) | null>(null);
  const [specialPost, setSpecialPost] = useState<null | { postId: string, isConvert2TaskMode?: boolean | null, isCreateSubTask?: boolean | null }>(null)

  const [resolverTaskRecurance, setResolverTaskRecurance] = useState<((value: any) => void) | null>(null);
  const [specialTaskRecurance, setSpecialTaskRecurance] = useState<null | { postId: string }>(null);

  // Basic show (same as previous example)
  const show = (options: BasicDialogOptions) => {
    return new Promise<any>((resolve) => {
      setOpts(options);
      setResolver(() => resolve);
    });
  };

  const confirm = (message: string, title?: string) => {
    return show({ message, title, okText: "OK", cancelText: "Cancel", showCancel: true })
      .then((v) => v === "ok");
  };

  // New: showConfirmPassword
  // apiUrl: endpoint to call for verification, expects JSON { username, password } -> { success: boolean, ... }
  const showConfirmPassword = (args?: { title?: string; apiUrl?: string }) => {
    return new Promise<boolean>((resolve) => {
      setSpecial({ type: "confirmPassword", apiUrl: args?.apiUrl, title: args?.title });
      // we reuse resolver to capture result
      setResolver(() => (val: any) => resolve(Boolean(val)));
    });
  };

  const showPermConfig = (args?: { permName: string, entryId?: number | null, permKey: string, defaultAllow?: boolean }) => {
    return new Promise<boolean>((resolve) => {
      setSpecialPerm({ permName: args?.permName ?? "", entryId: args?.entryId || null, permKey: args?.permKey || "", defaultAllow: args?.defaultAllow ?? false });
      setResolverPerm(() => (val: any) => resolve(Boolean(val)));
    });
  }


  const showPostInfo = (args?: { postId: string, isConvert2TaskMode?: boolean | null, isCreateSubTask?: boolean | null }) => {
    return new Promise<boolean>((resolve) => {
      setSpecialPost({ postId: args?.postId ?? "", isConvert2TaskMode: args?.isConvert2TaskMode, isCreateSubTask: args?.isCreateSubTask });
      setResolverPost(() => (val: any) => resolve(Boolean(val)));
    });
  }

  const showTaskRecurance = (args?: { postId: string }) => {
    return new Promise<string>((resolve) => {
      setSpecialTaskRecurance({ postId: args?.postId ?? "" });
      setResolverTaskRecurance(() => (val: any) => resolve(val));
    })
  }

  const close = (value: any) => {
    // console.log('close:', { value, resolver, resolverPerm, resolverPost });
    if (resolver) resolver(value);
    if (resolverPerm) resolverPerm(value);
    if (resolverPost) resolverPost(value);
    if (resolverTaskRecurance) resolverTaskRecurance(value);

    setOpts(null);
    setSpecial(null);
    setResolver(null);
    setSpecialPerm(null);
    setResolverPerm(null);

    setSpecialPost(null);
    setResolverPost(null);

    setSpecialTaskRecurance(null);
    setResolverTaskRecurance(null);
  };

  const contextValue: DialogContextType = {
    show,
    confirm,
    showConfirmPassword,
    showPermConfig,
    showPostInfo,
    showTaskRecurance
  };

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {/* basic modal */}
      {opts && <DialogPortal opts={opts} onClose={close} />}
      {/* special confirm-password modal */}
      {special && special.type === "confirmPassword" && (
        <ConfirmPasswordPortal title={special.title} apiUrl={special.apiUrl} onClose={close} />
      )}
      {specialPerm && (
        <PermConfigPortal permName={specialPerm.permName} entryId={specialPerm.entryId} permKey={specialPerm.permKey} defaultAllow={specialPerm.defaultAllow} onClose={close} />
      )}
      {specialPost && (
        <PostInfoPortal postId={specialPost.postId} isConvert2TaskMode={specialPost.isConvert2TaskMode} isCreateSubTask={specialPost.isCreateSubTask} onClose={close} />
      )}
      {specialTaskRecurance && (
        <TaskRecurancePortal postId={specialTaskRecurance.postId} onClose={close} />
      )}
    </DialogContext.Provider>
  );
};

/* ----------------- Basic Dialog Portal (simple) ----------------- */
function DialogPortal({ opts, onClose }: { opts: BasicDialogOptions; onClose: (v: any) => void }) {
  const { title, message, okText = "OK", cancelText = "Cancel", showCancel = false } = opts;
  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        {title && <h3 style={{ marginTop: 0 }}>{title}</h3>}
        <div style={{ margin: "1rem 0" }}>{message}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {showCancel && <button onClick={() => onClose("cancel")}>{cancelText}</button>}
          <button onClick={() => onClose("ok")}>{okText}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ------------- ConfirmPassword Portal (built-in form) ------------- */

function ConfirmPasswordPortal({ title, apiUrl, onClose }: { title?: string; apiUrl?: string; onClose: (v: any) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // default API path if not provided
  const endpoint = apiUrl ?? "/api/auth/confirm-password";

  const submit = async () => {
    onClose(true);
  };

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0 }}>{title ?? "Confirm password"}</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 13 }}>
            Username
            <input
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              placeholder="username"
            />
          </label>

          <label style={{ fontSize: 13 }}>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              placeholder="password"
            />
          </label>

          {error && <div style={{ color: "crimson", fontSize: 13 }}>{error}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={() => onClose(false)} disabled={loading}>Cancel</button>
            <button onClick={submit} disabled={loading}>{loading ? "Checking..." : "Confirm"}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PermConfigPortal({ permName, entryId, permKey, defaultAllow, onClose }: { permName: string, entryId: number | null, permKey: string, defaultAllow: boolean, onClose: (v: any) => void }) {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.Settings,
    (state: any) => state.Profile2,
    (st: SettingsState, p2) => ({
      activeTool: st.activeTool,
      isAdmin: p2.isAdmin,
      orgDllValueForPerm: st.orgDllValueForPerm,
    })
  );
  const { activeTool, isAdmin, orgDllValueForPerm } = useAppSelector(storeData);

  const [loading, setLoading] = useState(false);
  const [ddlDivison, setDdlDivison] = useState<any[] | null>(null);
  const [ddlDepartment, setDdlDepartment] = useState<any[] | null>(null);
  const [ddlPosition, setDdlPosition] = useState<any[] | null>(null);
  const [ddlUser, setDdlUser] = useState<any[] | null>(null);
  const [orgDllValueForPermLoading, setOrgDllValueForPermLoading] = useState<boolean>(false);

  const [permAllow, setPermAllow] = useState<boolean>(defaultAllow);
  const [selDivision, setSelDivision] = useState<any[]>([]);
  const [selDepartment, setSelDepartment] = useState<any[]>([]);
  const [selPosition, setSelPosition] = useState<any[]>([]);
  const [selUser, setSelUser] = useState<any[]>([]);

  const [ddlDepartmentFilter, setDdlDepartmentFilter] = useState<any[]>([]);
  const [ddlPositionFilter, setDdlPositionFilter] = useState<any[]>([]);
  const [ddlUserFilter, setDdlUserFilter] = useState<any[]>([]);

  const onDllItemSelected = (mode: "div" | "dep" | "pos" | "user", values: any[]) => {
    if (mode === "div") {
      setSelDivision(values);
    } else if (mode === "dep") {
      setSelDepartment(values);
    } else if (mode === "pos") {
      setSelPosition(values);
    } else if (mode == "user") {
      setSelUser(values);
    }
  }

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = ddlDepartment?.filter(x => selDiv.length <= 0 || selDiv.includes(x.divisionName));
    setDdlDepartmentFilter(selDep ?? []);
  }, [ddlDepartment, selDivision]);

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = selDepartment.map(e => e.departmentName);
    const selPos = ddlPosition?.filter(x => {
      const divMatch = selDiv.length <= 0 || selDiv.includes(x.divisionName);
      const depMatch = selDep.length <= 0 || selDep.includes(x.departmentName);
      return divMatch && depMatch
    });
    setDdlPositionFilter(selPos ?? []);
  }, [ddlPosition, selDivision, selDepartment]);

  useEffect(() => {
    const selDiv = selDivision.map(e => e.divisionName);
    const selDep = selDepartment.map(e => e.departmentName);
    const selPos = selPosition.map(e => e.positionName);
    const selUser = ddlUser?.filter(x => {
      const divMatch = selDiv.length <= 0 || selDiv.includes(x.divisionName);
      const depMatch = selDep.length <= 0 || selDep.includes(x.departmentName);
      const posMatch = selPos.length <= 0 || selPos.includes(x.positionName);
      return divMatch && depMatch && posMatch;
    });
    setDdlUserFilter(selUser ?? []);
  }, [ddlUser, selDivision, selDepartment, selPosition]);

  useEffect(() => {
    try {
      // console.log('orgDllValueForPerm: ', { orgDllValueForPerm });
      if (!orgDllValueForPerm && !orgDllValueForPermLoading) {
        setOrgDllValueForPermLoading(true);

        const api = new APIClient();
        api.fnPostForm(url.LOAD_DDL_VALUE_FOR_PERM)
          .then(rs => {
            dispatch(setOrgDdlValueForPerm(rs));
          })
          .catch(er => {
            Utils.HandleException(er, true);
          });
      }
    } catch (e) {
      Utils.HandleException(e, true);
    }
  });

  useEffect(() => {
    if (orgDllValueForPerm) {
      const distinctDiv = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.divisionName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.divisionName, value: item.divisionName };
              return [item.divisionName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.divisionName.localeCompare(b.divisionName));

      const distinctDep = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.departmentName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.departmentName, value: item.departmentName };
              return [item.departmentName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.departmentName.localeCompare(b.departmentName));

      const distinctPos = Array.from(
        new Map(
          orgDllValueForPerm
            .filter((item: any) => item.positionName) // Step 1: filter out empty name1
            .map((item: any) => {
              const updated = { ...item, label: item.positionName, value: item.positionName };
              return [item.positionName, updated];
            })
        ).values() // Get only the unique values
      ).sort((a: any, b: any) => a.positionName.localeCompare(b.positionName));

      const distinctEmp = (orgDllValueForPerm ?? []).sort((a: any, b: any) => a.displayName2.localeCompare(b.displayName2)).map((item: any) => ({ ...item, label: item.displayName2, value: item.userId }))
      setDdlDivison(distinctDiv);
      setDdlDepartment(distinctDep);
      setDdlPosition(distinctPos);
      setDdlUser(distinctEmp);
    }
  }, [orgDllValueForPerm]);

  useEffect(() => {
    if (entryId) {
      setLoading(true);
      const api = new APIClient();
      api.fnPostForm(url.LOAD_PERM_CONFIG2, { entryId: entryId })
        .then(rs => {
          // console.log('LOAD_PERM_CONFIG2:', rs);
          setPermAllow(rs.allow);
          setSelDivision((rs.divisions?.split('|') ?? []).map((x: any) => ({ divisionName: x })));
          setSelDepartment((rs.departments?.split('|') ?? []).map((x: any) => ({ departmentName: x })));
          setSelPosition((rs.positions?.split('|') ?? []).map((x: any) => ({ positionName: x })));
          setSelUser((rs.userIds?.split('|') ?? []).map((x: any) => ({ userId: Utils.ToNumber(x, -1) ?? -1 })));
          setLoading(false);
        })
        .catch(er => {
          Utils.HandleException(er, true);
          setLoading(false);
        });
    }
  }, [entryId]);

  const onSave = () => {
    try {
      setLoading(true);
      const api = new APIClient();
      const parm = {
        entryId: entryId,
        permName: permKey,
        allow: permAllow,
        divisions: selDivision.map(x => x.divisionName).join('|'),
        departments: selDepartment.map(x => x.departmentName).join('|'),
        positions: selPosition.map(x => x.positionName).join('|'),
        userIds: selUser.map(x => x.userId).join('|'),
      }
      // console.log(parm);
      api.fnPostForm(url.SAVE_PERM_CONFIG2, parm)
        .then(rs => {
          // console.log('SAVE_PERM_CONFIG2:', rs);
          onClose(true);
          setLoading(false);
        })
        .catch(er => {
          Utils.HandleException(er, true);
          setLoading(false);
        })
    } catch (e) {
      Utils.HandleException(e, true);
      setLoading(false);
    }
  }

  const onDel = () => {
    setLoading(true);
    new APIClient().fnPostForm(url.DEL_PERM_CONFIG2, { entryId: entryId })
      .then(rs => {
        setLoading(false);
        onClose(true);
      })
      .catch(er => {
        Utils.HandleException(er, true);
        setLoading(false);
      });

  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle}>
        {loading && <Loader />}
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--m2-line-divider-color)" }}>กำหนดสิทธิ์ - <span className="text-danger">{permName}</span></h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <FormControlLabel
                  control={<Switch checked={permAllow} onChange={(e) => setPermAllow(e.target.checked)} />}
                  label={permAllow ? (<span className="text-success">อนุญาต</span>) : (<span className="text-danger">ไม่อนุญาติ</span>)} />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="หน่วยงาน"
                  options={ddlDivison as any[]}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("div", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  //value={item.division ? ddlDivison?.find(t => t.value === item.division) : null}
                  value={ddlDivison?.filter(x1 => selDivision.map(x => x.divisionName).includes(x1.divisionName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">

              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="แผนก"
                  options={ddlDepartmentFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("dep", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlDepartment?.filter(x1 => selDepartment.map(x => x.departmentName).includes(x1.departmentName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">

              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="ตำแหน่ง"
                  options={ddlPositionFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("pos", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlPosition?.filter(x1 => selPosition.map(x => x.positionName).includes(x1.positionName))}
                  isClearable={true}
                ></Select>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <Select
                  isMulti={true}
                  placeholder="พนักงาน"
                  options={ddlUserFilter}
                  styles={Utils.GetThemeForReactSelect()}
                  onChange={(e: any) => onDllItemSelected("user", e)}
                  // onChange={(e: any) => setPermData(prev => prev.map(p2 => (p2.uid !== item.uid) ? p2 : { ...p2, division: e?.value }))}
                  value={ddlUser?.filter(x1 => selUser.map(x => x.userId).includes(x1.userId))}
                  isClearable={true}
                ></Select>
              </div>
              {/* <div className="col">
                <input type="text" className="form-control" placeholder="พนักงาน" />
              </div>                                                   */}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "dashed 1px var(--m2-line-divider-color)" }}>
            <div>
              <button className="btn btn-danger" onClick={onDel} disabled={loading}>ลบ</button>
            </div>
            <div>
              <button className="btn btn-warning" onClick={() => onClose(false)} disabled={loading}>ยกเลิก</button>
              <button className="btn btn-primary" onClick={onSave} disabled={loading}>บันทึก</button>
            </div>
            {/* <button onClick={submit} disabled={loading}>{loading ? "Checking..." : "Confirm"}</button> */}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PostInfoPortal({ isConvert2TaskMode, isCreateSubTask, postId, onClose }: { isConvert2TaskMode?: boolean | null, isCreateSubTask?: boolean | null, postId: string, onClose: (v: any) => void }) {
  const { dispatch, useAppSelector } = useRedux();
  const storeData = createSelector(
    (state: any) => state.WpSlice,
    (state: any) => state.Profile2,
    (wp: WpSliceData, p2: any) => ({
      // activePostInfo: wp.activePostInfo,
      curUserId: p2.userId,
      isAdmin: p2.isAdmin,
    })
  );
  const { /*activePostInfo,*/ curUserId, isAdmin } = useAppSelector(storeData);
  const [wpMode, setWpMode] = useState<WpModes | null>(null);
  const [taskId, setTaskId] = useState<string>("");
  const [members, setMembers] = useState<any[]>([]);
  const [dataChange, setDataChange] = useState<boolean>(false);
  const [tags, setTags] = useState<WpPostInfoTag[]>([]);
  const [title, setTitle] = useState<string>("");
  const [projectId, setProjectId] = useState<number | null>(null);
  const [assignerId, setAssignerId] = useState<number | null>(null);
  const [respondId, setRespondId] = useState<number | null>(null);
  const [planStartDate, setPlanStartDate] = useState<Date | null>(null);
  const [planFinishedDate, setPlanFinishedDate] = useState<Date | null>(null);
  const [requestBy, setRequestBy] = useState<number | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);

  useEffect(() => {
    try {
      setLoading(true);
      loadPostInfoApi(postId)
        .then(rs => {
          const activePostInfo = rs;
          setWpMode(activePostInfo.wpMode);
          setMembers(activePostInfo.members ?? []);
          setTaskId(activePostInfo.postId ?? "");
          setTags(activePostInfo.tags);
          setTitle(activePostInfo.title);
          setProjectId(activePostInfo.projectId ?? null);
          setAssignerId((isConvert2TaskMode == true || isCreateSubTask == true) ? curUserId : (activePostInfo.assignerId ?? null));
          setRespondId(activePostInfo.respondId ?? null);
          setPlanStartDate(activePostInfo.planStartDate ?? null);
          setPlanFinishedDate(activePostInfo.planFinishedDate ?? null);
          setRequestBy(activePostInfo.requestBy ?? null);
          setTaskStatus(activePostInfo.taskStatus);
          setLoading(false);
        })
        .catch(e2 => {
          Utils.HandleException(e2, true);
          setLoading(false);
        });
    } catch (e) {
      Utils.HandleException(e, true);
      setLoading(false);
    }
  }, []);

  const [taskHist, setTaskHist] = useState<any[]>([]);
  useEffect(() => {
    if (wpMode == WpModes.Task && taskId) {
      try {
        const api = new APIClient;
        api.fnPostForm(url.WP_LOAD_TASK_HIST, { postId: taskId })
          .then(rs => {
            setTaskHist(rs);
          })
          .catch(e2 => { Utils.HandleException(e2, true); })
      } catch (e1) {
        Utils.HandleException(e1, true);
      }
    }
  }, [wpMode, taskId]);

  const onRemoveTag = (tagId: number): void => {
    try {
      if (tagId <= 0) return;
      setTags(prev => prev.map(x => x.tagId == tagId ? ({ ...x, category: "--del--" }) : (x)));
      setDataChange(true);
    } catch (e) {
      Utils.HandleException(e, true);
    }
  }

  // useEffect(() => {
  //   console.log('tags:', tags);
  // }, [tags]);


  // console.log('activePostInfo:', activePostInfo);

  const [loading, setLoading] = useState(false);
  const [userDll, setUserDll] = useState<any[]>([]);

  useEffect(() => {
    try {
      const api = new APIClient();
      api.fnPostForm(url.GET_DLL_VALUE_USER)
        .then(rs => {
          // console.log('GET_DLL_VALUE_USER:', rs);
          setUserDll(rs);
        })
        .catch(ex => {
          Utils.HandleException(ex, false);
        });
    } catch (e) {
      Utils.HandleException(e, false);
    }
  }, [])

  const isRoleMatch = (memberRole: MemberRole[]): boolean => {
    const myRole = "," + (members.find(x => x.memberId == curUserId)?.memberRoleN) + ",";
    const found = memberRole.find(x => myRole.includes("," + x + ","));
    // console.log('isRoleMatch:', { memberRole, myRole, found });
    return found ? true : false;
  }

  const isTask = wpMode == WpModes.Task || (isConvert2TaskMode == true) || (isCreateSubTask == true);
  const canEditTaskTitle = /*isTask &&*/ (isAdmin || (isConvert2TaskMode == true) || (isCreateSubTask == true) || isRoleMatch([MemberRole.TaskAssigner, MemberRole.TaskCreator, MemberRole.TaskMine, MemberRole.TaskReqBy]));
  const canEditTaskProject = canEditTaskTitle;
  const canEditTaskAssigner = false;
  const canEditTaskRespond = canEditTaskTitle;
  const canEditTaskDate = canEditTaskTitle;
  const canEditTaskStatus = canEditTaskTitle;
  const canEditTaskRequester = canEditTaskTitle;
  const youIsMember = members?.findIndex(x => x.memberId == curUserId) >= 0;

  const onSave = async () => {
    try {
      if (!title || title.trim() === "") {
        alert('กรุณาระบุ ' + (wpMode == WpModes.Task ? 'ชื่องาน' : 'หัวข้อโพส'));
        return;
      }
      if ((isConvert2TaskMode == true || isCreateSubTask == true || wpMode == WpModes.Task) && respondId == null) {
        alert('กรุณาระบุ เจ้าของงาน');
        return;
      }
      setLoading(true);
      const parm: SavePostInfoParm = {
        deviceId: Utils.GetDeviceId(),
        postIdTop: taskId,
        title,
        projectId,
        assignerId,
        respondId,
        planStartDate,
        planFinishedDate,
        requestBy,
        isConvert2TaskMode: isConvert2TaskMode == true ? true : false,
        isCreateSubTask: isCreateSubTask == true ? true : false,
        taskStatus: Utils.ToNumber(taskStatus, TaskStatus.ToDo as number),
        tagRemove: tags?.filter(x => x.category == "--del--").map(y => y.tagId ?? -1)
      };
      const post = await savePostInfo(parm);
      // console.log('save post rs:', post);
      if (isConvert2TaskMode == true) {
        dispatch(removePostTitleFromList(taskId));
        dispatch(changeTab(TABS.TASK));
      }
      dispatch(setPostInfoOnSignalR(post, post.lastChatText, post.lastChatTime, post.lastChatImg, post.unreadCount, (post.memberRoles ?? "").split('|')));
      setLoading(false);
      onClose(true);
    } catch (e) {
      setLoading(false);
      Utils.HandleException(e, true);
    }
  }


  interface ChangeInfoProp {
    info: any;
  }

  const ChangeInfo: React.FC<ChangeInfoProp> = ({ info }) => {
    switch (info.fieldName) {
      case "*": return (<>สร้างงาน <span className="a-tag-wrap">{info.valueNew}</span></>);
      case "ProjectId": return (<>เปลี่ยน&nbsp;<span className="text-primary">โครงการ</span>&nbsp;เป็น&nbsp;<span className="a-tag-wrap"> {info.project1 ? (info.project1) : "ไม่มี"} </span></>);
      case "TaskName": return (<>เปลี่ยน&nbsp;<span className="text-primary">ชื่องาน</span>&nbsp;เป็น&nbsp;<span className="a-tag-wrap"> {info.valueNew ? (info.valueNew) : "ไม่มี"} </span></>);
      case "RespondId":
      case "RequestBy":
      case "AssignerId":
      case "CreateBy": {
        const cap = info.fieldName?.replace("RespondId", "เจ้าของงาน").replace("RequestBy", "ผู้ขอ").replace("AssignerId", "ผู้มอบหมาย").replace("CreateBy", "ผู้สร้างงาน");
        return (
          <div className="d-flex justify-content-start">
            เปลี่ยน&nbsp;<span className="text-primary">{cap}</span>
            &nbsp;จาก&nbsp;<span className="text-secondary"> {info.u0UserId ? (info.u0Name) : "ไม่มี"} </span>
            &nbsp;เป็น&nbsp;<span className="a-tag-wrap"> {info.u1UserId ? (info.u1Name) : "ไม่มี"} </span>
          </div>
        );
      }
      case "PlanFinishedDate":
      case "ActFinishedDate":
      case "PlanStartDate":
      case "ActStartDate":
        const cap = info.fieldName?.replace("PlanFinishedDate", "กำหนดเสร็จ").replace("ActFinishedDate", "วันเสร็จ").replace("PlanStartDate", "วันเริ่ม").replace("ActStartDate", "วันเริ่ม");
        return (
          <div className="d-flex justify-content-start">
            เปลี่ยน&nbsp;<span className="text-primary">{cap}</span>
            &nbsp;จาก&nbsp;<span className="text-secondary"> {info.valueOld ? (info.valueOld) : "ไม่มี"} </span>
            &nbsp;เป็น&nbsp;<span className="a-tag-wrap"> {info.valueNew ? (info.valueNew) : "ไม่มี"} </span>
          </div>
        );
        break;
      case "Status":
        const t0 = Utils.GetTaskStatusFromVal(Utils.ToNumber(info.valueOld, TaskStatus.ToDo)) ?? TaskStatus.ToDo;
        const t1 = Utils.GetTaskStatusFromVal(Utils.ToNumber(info.valueNew, TaskStatus.ToDo)) ?? TaskStatus.ToDo;
        // console.log('TaskStatus:', { t0, t1, v0: info.valueOld, v1: info.valueNew, xx: TaskStatus.ToDo });
        return (
          <div className="d-flex justify-content-start">
            เปลี่ยน&nbsp;<span className="text-primary">สถานะ</span>
            &nbsp;จาก&nbsp;
            <span className={`badge-task-status-head badge-task-status-${TaskStatus[t0 ?? TaskStatus.ToDo].toLowerCase()}`}>
              {Utils.GetTaskStatusIcon(t0)}
              {Utils.GetTaskStatusText(t0)}
            </span>
            &nbsp;เป็น&nbsp;
            <span className={`badge-task-status-head badge-task-status-${TaskStatus[t1 ?? TaskStatus.ToDo].toLowerCase()}`}>
              {Utils.GetTaskStatusIcon(t1)}
              {Utils.GetTaskStatusText(t1)}
            </span>
          </div>
        );
        break;
      default: return `${info.fieldName} from [${info.valueOld}] to [${info.valueNew}]`;
    }
  }

  const [viewTabMember, setViewTabMember] = useState<boolean>(true);

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {loading && (<Loader />)}
      <div style={modalStyle}>
        {/* {loading && <Loader />} */}
        {(isConvert2TaskMode == true) && (<><h3><i className="fas fa-hammer"></i>เปลี่ยน Post เป็น Task</h3></>)}
        {(isCreateSubTask == true) && (<><h3><i className="fas fa-hammer"></i>สร้างงานย่อย</h3></>)}
        {(!(isConvert2TaskMode == true) && !(isCreateSubTask == true)) && (
          <h3 style={{
            marginTop: 0, borderBottom: "dashed 1px var(--m2-line-divider-color)", whiteSpace: "nowrap", overflowX: "auto", overflow: "hidden"
          }}>{title}</h3>
        )}
        {/* <AutoTextArea value="คำว่า Title (ไทเทิล) โดยทั่วไปหมายถึง ชื่อเรื่อง, หัว ข้อ, หรือคำนำหน้า ซึ่งมีความสำคัญมากในการทำ SEO บนเว็บไซต์ (Title Tag) เพื่อใช้บ่งบอกเนื้อหาหน้าเว็บให้ค้นเจอได้ง่าย และแสดงผลบนแถบแท็บเบราว์เซอร์หรือหน้าผลการค้นหาของ Google รวมถึงใช้เป็นคำนำหน้าชื่อ (Mr., Ms.) หรือตำแหน่งยศ/บรรดาศักดิ์ได้ด้วย. " /> */}
        <div className="container-fluid" style={{ maxHeight: "calc(100vh - 200px)", overflowX: "hidden", overflowY: "auto" }}>
          <div className="row">
            {/* allow to edit task name and post title */}
            <div className="col-12">
              {wpMode === WpModes.Task ? "ชื่องาน" : "หัวข้อโพส"}
              <AutoTextArea value={title} readOnly={!canEditTaskTitle} onChange={(e) => { setTitle(e.target.value); setDataChange(true); }} />
            </div>
          </div>
          {(wpMode === WpModes.Task || (isConvert2TaskMode == true) || (isCreateSubTask == true)) && (
            <div className="row">
              {/* <div className="col-12">
                งาน
                <AutoTextArea value={title} readOnly={!canEditTaskTitle} onChange={(e) => { setTitle(e.target.value); setDataChange(true); }} />
              </div> */}
              <div className="col-12">
                โครงการ
                <SelectDropdownTkProj isDisabled={!canEditTaskProject} selectedProjectId={projectId} onProjectChanged={(pid) => { setProjectId(pid); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                ผู้มอบหมาย
                <SelectDropdownEmp isDisabled={!canEditTaskAssigner} selectedUserId={assignerId} onUserChanged={(e) => { setAssignerId(e); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                เจ้าของงาน
                <SelectDropdownEmp isDisabled={!canEditTaskRespond} selectedUserId={respondId} onUserChanged={(e) => { setRespondId(e); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                วันเริ่ม
                <DatePickerEx isDisabled={!canEditTaskDate} selectedDate={planStartDate ?? null} onDateChanged={(date) => { setPlanStartDate(date); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                กำหนดเสร็จ
                <DatePickerEx isDisabled={!canEditTaskDate} selectedDate={planFinishedDate ?? null} onDateChanged={(date) => { setPlanFinishedDate(date); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                ผู้ขอ
                <SelectDropdownEmp isDisabled={!canEditTaskRequester} selectedUserId={requestBy} onUserChanged={(e) => { setRequestBy(e); setDataChange(true); }} />
              </div>
              <div className="col-12 col-sm-6">
                สถานะ
                <SelectDropdownTaskStatus isDisabled={!canEditTaskStatus} selectedStatus={taskStatus ?? TaskStatus.ToDo} onStatusChanged={(val) => { setTaskStatus(val); setDataChange(true); }} />
              </div>
            </div>
          )}


          {!(isCreateSubTask == true) && (
            <div className="row mt-2">
              <div className="col-12" >
                {/* <span className="bs-badge bs-badge-secondary fz-10">สมาชิก</span> */}
                <div className="d-flex justify-content-start" style={{ gap: "4px" }}>
                  <button className={"btn btn-sm btn-" + (viewTabMember ? "" : "outline-") + "primary"} onClick={(e) => { if (wpMode == WpModes.Task) setViewTabMember(prev => !prev); }}>สมาชิก</button>
                  {wpMode == WpModes.Task && (<button className={"btn btn-sm btn-" + (viewTabMember ? "outline-" : "") + "primary"} onClick={(e) => setViewTabMember(prev => !prev)}>ประวัติ</button>)}
                </div>
                <div style={{ maxHeight: "280px", overflow: "auto" }}>

                  {viewTabMember && (
                    <>
                      {members.map((mem, index) => (
                        <div key={index} className={"d-flex justify-content-between hover" + (mem.isActive ? "" : " user-inactive")}>
                          <div className="d-flex justify-content-start align-items-center">
                            <UserAvatarWithName
                              userId={mem.memberId}
                              abbrName={mem.abbrName}
                              keyValue={index}
                              profileImage={mem.profileImage}
                              foreColor={mem.foreColor}
                              backColor={mem.bgColor}
                              displayName2={mem.displayName2}
                            />
                            {mem.memberId === curUserId && (<span className="badge badge-soft-primary rounded p-1 ml-1 bg-secondary">you</span>)}
                          </div>
                          <div>{mem.memberRole}</div>
                        </div>
                      ))}
                    </>
                  )}

                  {!viewTabMember && (
                    <div >
                      {taskHist.map((hist, index) => (
                        <div className="mt-2">
                          <div className="d-flex justify-content-start">
                            <UserAvatarWithName
                              keyValue={index}
                              userId={hist.chgUserId}
                              abbrName={hist.chgAbbr}
                              profileImage={hist.chgImage}
                              foreColor={hist.chgForeColor}
                              backColor={hist.chgBgColor}
                              displayName2={hist.chgName}
                            />
                            <span className="ms-2">{Utils.GetShortDateTimeToShowInChat(hist.changeDate, false)}</span>
                          </div>
                          <div className="wp-conver-content ms-4 me-2" style={{ backgroundColor: "var(--bs-input-bg)", paddingBottom: "6px" }}>
                            <ChangeInfo info={hist} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12">
                <div style={{ height: "5px" }}>
                  {/* <span className="bs-badge bs-badge-secondary">tag</span> */}
                </div>
                {tags?.filter(x => x.category != "--del--").map((tag, index) => (
                  <WpTagBadge key={index} keyId={index} backColor={tag.bgColor} foreColor={tag.faColor} faIcon={tag.faIcon} tagId={tag.tagId ?? -1} tagName={tag.name ?? ""}
                    suffixIcon={youIsMember ? "fas fa-minus-circle text-danger" : null} onSuffixClick={onRemoveTag}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, borderTop: "dashed 1px var(--m2-line-divider-color)" }}>
          <div>
            {/* <button className="btn btn-danger" onClick={onDel} disabled={loading}>ลบ</button> */}
          </div>
          <div className="d-flex justify-content-between" style={{ gap: "6px" }}>
            <button className="btn btn-warning" onClick={() => onClose(false)} disabled={loading}>ปิด</button>
            {((isConvert2TaskMode == true || isCreateSubTask == true) || (youIsMember && dataChange)) && (
              <button className="btn btn-primary" onClick={onSave} disabled={loading}>{isConvert2TaskMode == true ? "เปลี่ยน post เป็น task" : "บันทึก"}</button>
            )}

          </div>
          {/* <button onClick={submit} disabled={loading}>{loading ? "Checking..." : "Confirm"}</button> */}
        </div>
        {/* </div> */}
      </div>
    </div>,
    document.body
  );
}

// /* ---------------- Styles (minimal) ---------------- */
// export const backdropStyle: React.CSSProperties = {
//   position: "fixed",
//   inset: 0,
//   background: "rgba(0,0,0,0.6)",
//   display: "flex",
//   justifyContent: "center",
//   alignItems: "center",
//   zIndex: 1000,
// };

// export const modalStyle: React.CSSProperties = {
//   background: "var(--m2-wp-conver-content-bg)",
//   border: "solid 1px var(--m2-wp-conver-content-border)",
//   padding: 16,
//   borderRadius: 8,
//   width: "100%",
//   maxWidth: 768,
//   boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
// };

// const inputStyle: React.CSSProperties = {
//   width: "100%",
//   padding: "8px 10px",
//   marginTop: 6,
//   borderRadius: 6,
//   border: "1px solid #ccc",
// };
